import WarehouseStockData from '../../model/warehouse-stock-data';
import Tooltip from './tooltip';
import MessageHelper from '../../helpers/message-helper';
import { StockStatus } from '../../model/stock-and-price-data';

const STOCK_LEVEL_ENDPOINT = '/product/stock';

export default class StockAvailability {
  static readonly ASYNC_KEY = 'stockAvailability';
  static readonly SELECTOR = '.js-stockAvailability';

  private readonly AVAILABLE_CLASS = '-available';
  private readonly UNAVAILABLE_CLASS = '-unavailable';
  private readonly CENTRAL_AVAILABLE_CLASS = '-central-available';
  private readonly REGIONAL_AVAILABLE_CLASS = '-regional-available';
  private readonly DIRECT_DELIVERY_AVAILABLE = '-direct-delivery-available';
  /* eslint-disable max-len */
  private readonly AVAILABLE_ICON_TEMPLATE = `<img class="icon -small" src="${window.ACC.config.commonResourcePath}/images/icons/check-circle-green-bold.svg" alt=""/>`;
  private readonly CENTRAL_AVAILABLE_ICON_TEMPLATE = `<img class="icon -small" src="${window.ACC.config.commonResourcePath}/images/icons/circle-green-bold.svg" alt=""/>`;
  private readonly REGIONAL_AVAILABLE_ICON_TEMPLATE = `<img class="icon -small" src="${window.ACC.config.commonResourcePath}/images/icons/question-circle-orange-bold.svg" alt=""/>`;
  private readonly EXTERNAL_UNAVAILABLE_ICON_TEMPLATE = `<img class="icon -small" src="${window.ACC.config.commonResourcePath}/images/icons/question-circle-orange-bold.svg" alt=""/>`;
  private readonly NOT_AVAILABLE_ICON_TEMPLATE = `<img class="icon -small" src="${window.ACC.config.commonResourcePath}/images/icons/cross-circle-grey-bold.svg" alt=""/>`;
  /* eslint-enable max-len */

  private readonly productCode: string;
  private readonly quantityInput: HTMLInputElement;
  private readonly productDirectDelivery: boolean;
  private productDirectDeliveryInStock = false;
  private quantityValue = 1;

  private readonly stockAvailabilityIcon: HTMLElement;
  private stockAvailabilityTooltip?: Tooltip;

  private warehouseData?: WarehouseStockData[];
  private errorCheckingStock = false;
  private inStockStatus: StockStatus = 'NOT_IN_STOCK';

  constructor(private container: HTMLDivElement, warehouseData?: WarehouseStockData[]) {
    this.stockAvailabilityIcon = container.querySelector('.js-stockAvailabilityIcon') as HTMLElement;
    this.setStockStatus(warehouseData);
    const {
      code,
      quantityInputSelector,
      availableWarehouses,
      reactive,
      directDelivery,
    } = container.dataset;

    if (!code || !quantityInputSelector) {
      throw new Error('No quantity input selector or product code provided for stock check');
    }
    this.productCode = code;
    this.productDirectDelivery = directDelivery === 'true';

    const result = document.querySelector<HTMLInputElement>(quantityInputSelector);
    if (result === null || !(result instanceof HTMLInputElement)) {
      throw new Error(`No input found matching query ${quantityInputSelector}`);
    }
    this.quantityInput = result;
    this.quantityValue = parseInt(this.quantityInput.value, 10);
    if (reactive && JSON.parse(reactive)) {
      this.bindQuantityInputChange();
    }

    if (availableWarehouses) {
      const parsedWarehouses: unknown = JSON.parse(decodeURIComponent(availableWarehouses));
      this.warehouseData = Array.isArray(parsedWarehouses) ? parsedWarehouses as WarehouseStockData[] : [];
      this.render();
    } else if (warehouseData) {
      this.warehouseData = warehouseData;
      this.render();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.fetchStockLevel().then(() => this.render());
    }
  }

  private bindQuantityInputChange(): void {
    this.quantityInput.addEventListener('input', () => {
      this.quantityValue = parseInt(this.quantityInput.value, 10);
      this.render();
    });
  }

  private getUrl(): string {
    return `${window.ACC.config.encodedContextPath}${STOCK_LEVEL_ENDPOINT}?productCode=${this.productCode}`;
  }

  private fetchStockLevel(): Promise<void> {
    this.errorCheckingStock = false;
    return fetch(this.getUrl())
      .then((result) => {
        if (!result.ok) {
          this.errorCheckingStock = true;
        }
        return result.json();
      })
      .then((stockAvailabilities: WarehouseStockData[]) => {
        this.warehouseData = stockAvailabilities;
      })
      .catch(() => {
        this.errorCheckingStock = true;
      });
  }

  render(): void {
    if (this.stockAvailabilityTooltip) {
      this.stockAvailabilityTooltip.destroy();
    }

    if (this.errorCheckingStock) {
      this.stockAvailabilityIcon.innerText = '-';
      return;
    }

    this.setStockStatus(this.warehouseData);
    const warehouses = this.productDirectDelivery
      ? this.filterExternalWarehouse(this.warehouseData) : this.removeExternalAndRegionalWarehouses(this.sortWarehouses(this.warehouseData));

    let tooltipContent = `
        <ul class="warehouse-stock-list">
            ${warehouses.map((warehouse) => this.buildWarehouseStockTemplate(warehouse)).join('')}
        </ul>
    `;

    this.container.classList.remove(
      this.UNAVAILABLE_CLASS,
      this.AVAILABLE_CLASS,
      this.CENTRAL_AVAILABLE_CLASS,
      this.REGIONAL_AVAILABLE_CLASS,
      this.DIRECT_DELIVERY_AVAILABLE,
    );

    switch (this.inStockStatus) {
      case 'NOT_IN_STOCK':
        this.container.classList.add(this.UNAVAILABLE_CLASS);
        this.stockAvailabilityIcon.innerHTML = this.REGIONAL_AVAILABLE_ICON_TEMPLATE;
        break;
      case 'CENTRAL_STOCK':
        this.container.classList.add(this.CENTRAL_AVAILABLE_CLASS);
        this.stockAvailabilityIcon.innerHTML = this.CENTRAL_AVAILABLE_ICON_TEMPLATE;
        break;
      case 'REGIONAL_STOCK':
        this.container.classList.add(this.REGIONAL_AVAILABLE_CLASS);
        this.stockAvailabilityIcon.innerHTML = this.REGIONAL_AVAILABLE_ICON_TEMPLATE;
        tooltipContent += `<p class="pt-5">${MessageHelper.getMessage('eparts.productDetails.regional.available.info')}</p>`;
        break;
      case 'EXTERNAL_STOCK':
        this.container.classList.add(this.DIRECT_DELIVERY_AVAILABLE);
        this.stockAvailabilityIcon.innerHTML = this.AVAILABLE_ICON_TEMPLATE;
        break;
      default:
        this.container.classList.add(this.AVAILABLE_CLASS);
        this.stockAvailabilityIcon.innerHTML = this.AVAILABLE_ICON_TEMPLATE;
    }

    this.stockAvailabilityTooltip = new Tooltip(this.container, tooltipContent);
  }

  // eslint-disable-next-line class-methods-use-this
  private sortWarehouses(warehouses: WarehouseStockData[] = []): WarehouseStockData[] {
    // sort warehouses in order: LOCAL, REMOTE, REGIONAL
    return [...warehouses].sort((a, b) => {
      if (a.type === b.type) {
        return 0;
      }
      if (a.type === 'LOCAL') {
        return -1;
      }
      if (b.type === 'LOCAL') {
        return 1;
      }
      if (a.type === 'REMOTE') {
        return -1;
      }
      return 1;
    });
  }

  // eslint-disable-next-line class-methods-use-this
  private removeExternalAndRegionalWarehouses(warehouses: WarehouseStockData[] = []): WarehouseStockData[] {
    return warehouses.filter((item) => (item.type !== 'EXTERNAL' && item.type !== 'REGIONAL'));
  }

  // eslint-disable-next-line class-methods-use-this
  private filterExternalWarehouse(warehouses: WarehouseStockData[] = []): WarehouseStockData[] {
    return warehouses.filter((item) => item.type === 'EXTERNAL');
  }

  private setStockStatus(warehouses: WarehouseStockData[] = []): void {
    const {
      localStock,
      centralStock,
      regionalStock,
      externalStock,
    } = warehouses.reduce((acc, warehouse) => {
      switch (warehouse.type) {
        case 'LOCAL':
          acc.localStock += warehouse.stockLevelAmount;
          break;
        case 'REMOTE':
          acc.centralStock += warehouse.stockLevelAmount;
          break;
        case 'EXTERNAL':
          acc.externalStock += warehouse.stockLevelAmount;
          this.productDirectDeliveryInStock = warehouse.stockLevelAmount > 0;
          break;
        default:
          acc.regionalStock += warehouse.stockLevelAmount;
          break;
      }
      return acc;
    }, {
      localStock: 0,
      centralStock: 0,
      regionalStock: 0,
      externalStock: 0,
    });

    if (this.productDirectDelivery) {
      this.inStockStatus = this.quantityValue <= externalStock ? 'EXTERNAL_STOCK' : 'NOT_IN_STOCK';
    } else {
      // eslint-disable-next-line no-lonely-if
      if (this.quantityValue <= localStock) {
        this.inStockStatus = 'IN_STOCK';
      } else if (this.quantityValue <= centralStock) {
        this.inStockStatus = 'CENTRAL_STOCK';
      } else if (this.quantityValue <= regionalStock) {
        this.inStockStatus = 'REGIONAL_STOCK';
      } else {
        this.inStockStatus = 'NOT_IN_STOCK';
      }
    }
  }

  private buildWarehouseStockTemplate(warehouse: WarehouseStockData): string {
    const icon = this.getWarehouseIcon(warehouse);
    const nameLabel = this.getNameLabel(warehouse);
    const stockMessage = this.getStockMessage(warehouse);

    return `
      <li class="warehouse-stock">
        <div class="warehouse-stock__icon">${icon}</div>
        <div class="warehouse-stock__label">
          <p class="warehouse-stock__name">${nameLabel}</p>
          ${stockMessage.length ? `<p class="warehouse-stock__stock">${stockMessage}</p>` : ''}
        </div>
      </li>
    `;
  }

  private getWarehouseIcon(warehouse: WarehouseStockData): string {
    let availabilityIcon = warehouse.stockLevelAmount > 0 ? this.AVAILABLE_ICON_TEMPLATE : this.NOT_AVAILABLE_ICON_TEMPLATE;
    if (this.productDirectDelivery) {
      availabilityIcon = this.productDirectDeliveryInStock ? this.AVAILABLE_ICON_TEMPLATE : this.EXTERNAL_UNAVAILABLE_ICON_TEMPLATE;
    }

    if (this.isLocalUnavailable(warehouse)) {
      availabilityIcon = this.REGIONAL_AVAILABLE_ICON_TEMPLATE;
    }

    return availabilityIcon;
  }

  private getStockMessage(warehouse: WarehouseStockData): string {
    const { stockLevelAmount } = warehouse;

    if (this.productDirectDelivery) {
      if (this.productDirectDeliveryInStock) {
        return `${MessageHelper.getMessageWithAttributes('eparts.warehouse.type.EXTERNAL.available.info', warehouse.stockLevelAmount)}`;
      }

      return `${MessageHelper.getMessage('eparts.warehouse.type.EXTERNAL.unavailable')}`;
    }

    if (this.isLocalUnavailable(warehouse)) {
      // TODO: Add message for due date
      return `${MessageHelper.getMessage('common.stock.unknownDate')}`;
    }

    if (stockLevelAmount === 0) {
      return '';
    }

    return `${MessageHelper.getMessageWithAttributes('common.stock.available', warehouse.stockLevelAmount)}`;
  }

  private getNameLabel(warehouse: WarehouseStockData): string {
    const {
      name, type,
    } = warehouse;

    if (this.productDirectDelivery) {
      if (this.productDirectDeliveryInStock) {
        return `${MessageHelper.getMessage('eparts.warehouse.type.EXTERNAL.available')}`;
      }

      return `${MessageHelper.getMessage('common.stock.unknownDate')}`;
    }

    return `${name} (${MessageHelper.getMessage(`eparts.warehouse.type.${type}`)})`;
  }

  private isLocalUnavailable(warehouse: WarehouseStockData): boolean {
    return warehouse.type === 'LOCAL' && this.inStockStatus !== 'IN_STOCK';
  }
}
